import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import { api } from "@/lib/api";
import { Carte, heure } from "./CartesAgent";

export const AgendaJour = ({ evenements }) => {
  const now = Date.now();
  const courant = evenements.find((e) => !e.fait && Math.abs(new Date(e.echeance) - now) < 30 * 60000);
  return (
    <Carte titre="📅 Mon agenda du jour" droite={`${evenements.length} rendez-vous`} testId="agenda-card" className="lg:col-span-4">
      {evenements.length === 0 && <p className="at-muted text-sm" data-testid="agenda-empty">Aucun rendez-vous aujourd'hui.</p>}
      <div className="space-y-1">
        {evenements.map((e) => (
          <Link key={e.id} to={e.fiche_id ? `/fiches/${e.fiche_id}` : "/agenda"} className="flex items-center gap-3 min-h-[48px] px-2 -mx-2" data-testid={`agenda-row-${e.id}`}
            style={{ background: e === courant ? "var(--selection, var(--surface-2))" : "transparent", borderRadius: "var(--radius-control)", opacity: e.fait ? 0.55 : 1 }}>
            <span className="text-sm at-muted tabular-nums w-14 shrink-0">{heure(e.echeance)}</span>
            <span className="min-w-0 flex-1"><b className="block text-sm truncate">{e.type_label} · {e.titre}</b>{e.note && <span className="block text-xs at-muted truncate">{e.note}</span>}</span>
            {e === courant && <span className="text-xs font-semibold shrink-0" style={{ color: "var(--accent)" }}>maintenant</span>}
          </Link>
        ))}
      </div>
    </Carte>
  );
};

const MEDAILLES = ["🥇", "🥈", "🥉"];

export const Challenge = ({ classement, objectif }) => {
  const total = classement.reduce((s, x) => s + x.opportunites, 0);
  const cible = Math.max(1, objectif * classement.length);
  return (
    <Carte titre="🏆 Challenge du jour" testId="challenge-card" className="lg:col-span-3">
      <p className="text-sm at-muted">Opportunités de l'équipe aujourd'hui</p>
      <div className="flex items-center gap-3 mt-2">
        <div className="h-2 at-pill flex-1 overflow-hidden" style={{ background: "var(--surface-3, var(--surface-2))" }}><span className="block h-full at-pill" style={{ width: `${Math.min(100, (100 * total) / cible)}%`, background: "var(--accent)" }} /></div>
        <b className="tabular-nums text-sm" data-testid="challenge-total">{total} / {cible}</b>
      </div>
      <div className="mt-4 space-y-1">
        {classement.map((c, i) => (
          <div key={c.nom} className="flex items-center gap-3 min-h-[40px] px-2 -mx-2 text-sm" data-testid={`ranking-row-${i}`}
            style={{ background: c.moi ? "var(--selection, var(--surface-2))" : "transparent", borderRadius: "var(--radius-control)" }}>
            <span className="w-6 text-center">{MEDAILLES[i] || i + 1}</span>
            <span className="at-avatar text-[11px]" style={{ width: 28, height: 28, background: c.moi ? "var(--primaire, var(--accent))" : "var(--surface-3, var(--surface-2))", color: c.moi ? "var(--sur-primaire, var(--accent-ink))" : "var(--ink)" }}>{c.nom.split(" ").map((x) => x[0]).join("").slice(0, 2)}</span>
            <span className={`flex-1 truncate ${c.moi ? "font-bold" : ""}`}>{c.nom}{c.moi ? " (vous)" : ""}</span>
            <b className="tabular-nums">{c.opportunites}</b>
          </div>
        ))}
      </div>
    </Carte>
  );
};

export const Activite = ({ heures, objectif }) => {
  const parHeure = objectif / heures.length;
  const max = Math.max(parHeure, ...heures.map((h) => h.appels), 1);
  return (
    <Carte titre="📈 Activité heure par heure" droite="appels · joints" testId="activity-card" className="lg:col-span-7">
      <div className="relative flex items-end gap-1 h-32">
        <span className="absolute inset-x-0 border-t border-dashed" style={{ bottom: `${(100 * parHeure) / max}%`, borderColor: "var(--muted)" }} title="Objectif par heure" />
        {heures.map((h) => (
          <div key={h.heure} className="flex-1 h-full flex items-end gap-px" title={`${h.heure} h : ${h.appels} appels, ${h.joints} joints`}>
            <span className="flex-1 rounded-t" style={{ height: `${(100 * h.appels) / max}%`, background: "var(--accent)", minHeight: h.appels ? 3 : 0 }} />
            <span className="flex-1 rounded-t" style={{ height: `${(100 * h.joints) / max}%`, background: "var(--menu, var(--ink))", minHeight: h.joints ? 3 : 0 }} />
          </div>
        ))}
      </div>
      <div className="flex gap-1 mt-1 text-[10px] at-muted tabular-nums">{heures.map((h) => <span key={h.heure} className="flex-1 text-center">{h.heure % 2 === 0 ? h.heure : ""}</span>)}</div>
    </Carte>
  );
};

export const MonPipeline = ({ processus }) => {
  const p = processus.find((x) => x.etapes.some((e) => e.nombre)) || processus[0];
  return (
    <Carte titre="🗂️ Mon pipeline" droite={p?.nom} testId="pipeline-card" className="lg:col-span-5">
      {!p && <p className="at-muted text-sm">Aucune fiche pour le moment.</p>}
      <div className="space-y-1">
        {p?.etapes.map((e) => (
          <Link key={e.code} to="/pipeline" className="flex items-center gap-3 min-h-[44px] text-sm" data-testid={`pipeline-step-${e.code}`}>
            <span className="w-1 self-stretch my-2 at-pill" style={{ background: e.temperature || "var(--muted)" }} />
            <span className="flex-1 truncate">{e.nom}</span>
            <b className="tabular-nums">{e.nombre}</b>
          </Link>
        ))}
      </div>
    </Carte>
  );
};

export const ATraiter = ({ d, recharger }) => {
  const vide = !d.manques.length && !d.annulations?.length && !d.rappels.length;
  const simuler = () => api.post("/poste/simuler-appel-manque", {}).then(recharger);
  return (
    <Carte titre="📥 À traiter maintenant" testId="todo-card" className="lg:col-span-7">
      {vide && <p className="at-muted text-sm" data-testid="todo-empty">Rien d'urgent pour le moment.</p>}
      <div className="space-y-2">
        {d.manques.map((m) => (
          <Link key={m.id} to={`/focus?prospect=${m.prospect_id}`} className="flex items-center gap-2 min-h-[44px] text-sm px-3 at-danger-doux" style={{ borderRadius: "var(--radius-control)" }} data-testid={`todo-missed-${m.id}`}>
            <Phone size={15} /><span className="flex-1 truncate">Appel manqué · <b>{m.nom}</b></span><span className="text-xs">{heure(m.created_at)}</span>
          </Link>
        ))}
        {d.rappels.map((t) => <Link key={t.id} to={t.prospect_id ? `/focus?prospect=${t.prospect_id}` : `/fiches/${t.fiche_id}`} className="flex items-center gap-2 min-h-[44px] text-sm" data-testid={`todo-callback-${t.id}`}><span className="text-xs at-muted tabular-nums w-12">{heure(t.echeance)}</span><span className="flex-1 truncate">Rappel · <b>{t.titre}</b></span></Link>)}
        {(d.annulations || []).map((a) => <Link key={a.id} to={`/fiches/${a.fiche_id}`} className="block min-h-[44px] text-sm px-3 py-2 at-alerte" style={{ borderRadius: "var(--radius-control)" }} data-testid={`todo-cancelled-${a.id}`}>Dossier annulé · <b>{a.fiche_nom}</b><span className="block text-xs truncate">{a.motif}</span></Link>)}
      </div>
      <button type="button" onClick={simuler} className="text-xs at-muted underline mt-3 min-h-[44px]" data-testid="simulate-missed-call">Simuler un appel entrant manqué</button>
    </Carte>
  );
};
